import {
  MAX_SERVICE_QUESTION_LENGTH,
  MAX_SERVICE_QUESTIONS,
  servicePriceRange,
  type ServiceQuestion,
} from '@acs/shared';
import type { ServiceDoc } from '../../shared/api/converters.js';
import type { ServiceEdit } from '../../shared/api/services.js';
import { ALL_DAY, ALL_DAY_STORED_MIN, type DurationChoice } from './serviceDuration.js';

/**
 * The service form's draft and its rules, without React (doc 64): what the add
 * and edit dialog holds while the owner types, and the values it saves.
 */

/** The highest price the schema and the rules accept. */
export const MAX_SERVICE_PRICE = 100_000;

/**
 * A price box's text as a number Firestore can store.
 *
 * `positiveNumeric` keeps the field usable WHILE typing, which means it must
 * allow states that are not yet numbers — typing "." leaves exactly ".", and
 * `Number('.')` is NaN. Firestore stores NaN happily; `ServiceSchema.parse`
 * then rejects it, and the gateway parses services on every inbound call — so
 * one stray keystroke in Settings would take the business's phone line down
 * until somebody re-saved the price.
 *
 * Empty means "no price given", not "free" — same as before.
 */
export function toPrice(raw: string): number {
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/**
 * Keep only what can still grow into a positive number.
 *
 * `<input type="number">` was doing this badly in three ways an owner meets
 * within a minute: a mouse wheel over a focused field silently changes the
 * price, `-` and `e` are accepted (`1e5` is a valid number), and clearing the
 * box yields `''`, which `Number('')` turns into a confident 0. Filtering the
 * text ourselves means the field can be empty while being typed in without
 * pretending that means free.
 *
 * `decimals: false` for minutes — half a minute is not a booking slot.
 */
export function positiveNumeric(raw: string, decimals: boolean): string {
  const cleaned = raw.replace(decimals ? /[^0-9.,]/g : /[^0-9]/g, '').replace(',', '.');
  if (!decimals) return cleaned.replace(/^0+(?=\d)/, '');
  // At most one separator, at most two places after it.
  const [whole, ...rest] = cleaned.split('.');
  const head = (whole ?? '').replace(/^0+(?=\d)/, '');
  return rest.length === 0 ? head : `${head}.${rest.join('').slice(0, 2)}`;
}

/**
 * One question row. `key` is made when the row is created — never during a
 * render — and is both its React key and the id it is saved under if it is new
 * or reworded. A saved question keeps its own id while its words are unchanged;
 * reworded, it is a NEW question, so an answer given to the old wording cannot
 * satisfy it (a manual booking form open in another tab, say).
 */
export interface QuestionRow {
  key: string;
  savedId: string | null;
  savedText: string | null;
  text: string;
}

/** The form's fields, held as TEXT while typed: a half-typed price is not a
 *  number, and forcing it through one is how an empty box becomes 0. */
export interface ServiceDraft {
  name: string;
  duration: DurationChoice;
  price: string;
  priceMax: string;
  questions: QuestionRow[];
}

/** A fresh question id: bounded, URL-safe, and what the rules accept. */
export function newQuestionId(): string {
  return crypto.randomUUID();
}

export function emptyDraft(): ServiceDraft {
  return { name: '', duration: 30, price: '', priceMax: '', questions: [] };
}

export function newQuestionRow(makeId: () => string = newQuestionId): QuestionRow {
  return { key: makeId(), savedId: null, savedText: null, text: '' };
}

export function draftOf(s: ServiceDoc, makeId: () => string = newQuestionId): ServiceDraft {
  const { from, to } = servicePriceRange(s);
  return {
    name: s.name,
    duration: s.allDay ? ALL_DAY : s.durationMin,
    // A stored 0 is shown as an empty box, as it was typed: "no price given".
    price: from ? String(from) : '',
    priceMax: to === null ? '' : String(to),
    questions: (s.questions ?? []).map((q) => ({ key: makeId(), savedId: q.id, savedText: q.text, text: q.text })),
  };
}

/** The question a row saves as: its stored id while unchanged, its own key once reworded. */
export function questionOf(row: QuestionRow): ServiceQuestion {
  const text = row.text.trim();
  const unchanged = row.savedId !== null && row.savedText !== null && text === row.savedText.trim();
  return { id: unchanged ? row.savedId! : row.key, text };
}

export type QuestionProblem = 'blank' | 'tooLong' | null;

/** What is wrong with one row — a row the owner added is never dropped silently. */
export function questionProblem(row: QuestionRow): QuestionProblem {
  const text = row.text.trim();
  if (text.length === 0) return 'blank';
  if (text.length > MAX_SERVICE_QUESTION_LENGTH) return 'tooLong';
  return null;
}

export interface DraftCheck {
  /** The values to save, or null while the draft cannot be saved. */
  edit: ServiceEdit | null;
  rangeInvalid: boolean;
  priceTooHigh: boolean;
  questionProblems: QuestionProblem[];
}

export function checkDraft(d: ServiceDraft): DraftCheck {
  const name = d.name.trim();
  const price = toPrice(d.price);
  const priceMax = d.priceMax.trim() === '' ? null : toPrice(d.priceMax);
  const rangeInvalid = priceMax !== null && priceMax <= price;
  const priceTooHigh = price > MAX_SERVICE_PRICE || (priceMax !== null && priceMax > MAX_SERVICE_PRICE);
  const questionProblems = d.questions.map(questionProblem);
  const valid =
    name.length > 0 &&
    name.length <= 80 &&
    !rangeInvalid &&
    !priceTooHigh &&
    d.questions.length <= MAX_SERVICE_QUESTIONS &&
    questionProblems.every((p) => p === null);
  return {
    edit: valid
      ? {
          name,
          durationMin: d.duration === ALL_DAY ? ALL_DAY_STORED_MIN : d.duration,
          allDay: d.duration === ALL_DAY,
          price,
          priceMax,
          questions: d.questions.map(questionOf),
        }
      : null,
    rangeInvalid,
    priceTooHigh,
    questionProblems,
  };
}

/**
 * Has anything the owner would lose changed? Compared on normalised values, so
 * adding a question row and removing it again — or retyping a question back to
 * its saved words — is no change.
 */
export function draftChanged(initial: ServiceDraft, draft: ServiceDraft): boolean {
  const norm = (d: ServiceDraft) =>
    JSON.stringify({
      name: d.name.trim(),
      duration: d.duration,
      price: d.price.trim(),
      priceMax: d.priceMax.trim(),
      questions: d.questions.map((r) => questionOf(r)),
    });
  return norm(initial) !== norm(draft);
}
